import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { activeRuns, cancelRun, getMessages, type RunInfo } from '../lib/conversations'
import { streamRun, type StreamHandle } from '../lib/runStream'
import { consumeVoiceReply, speakViaServer } from '../lib/speech'
import * as store from '../lib/streamStore'
import { listen, publish, requestLeadership, type Leadership } from '../lib/tabSync'
import { handleToolRequest } from '../lib/toolRunner'
import { useSession } from '../session/SessionProvider'

/**
 * Tum akis okuyucularinin SAHIBI. Router outlet'inin USTUNDE yasiyor.
 *
 * ## Neden bir route bileseninin ICINDE degil
 *
 * Bu, P4'un en onemli yapisal kisiti. Baska bir konusmaya gitmek, ayarlari
 * acmak veya sidebar'i kapatmak bir uretimi YIKMAMALI. Naif hali:
 *
 *     useEffect(() => { const h = stream(runId); return () => h.abort() }, [convId])
 *
 * ...ve bu kisiti ihlal ediyor: `<ConversationRoute>`un unmount'u bir iptal
 * sinyaline donusuyor. O hata dev'de GORUNMUYOR (tek sohbette gezinmiyorsun)
 * ve kullanimda cildirtici oluyor - kullanici sekme degistirip dondugunde
 * yaniti yarim buluyor.
 *
 * Burada okuyucular bir `ref` icinde ve YALNIZCA su durumlarda birakiliyor:
 * terminal cerceve, acik kullanici iptali, ya da sayfa kapanmasi.
 *
 * ## Acilista resume
 *
 * `GET /api/runs/active` cagriliyor: sayfa yenilendikten sonra hangi run'a
 * baglanacagini bilmenin TEK yolu bu (run id daha once yalnizca akisin
 * icinde geliyordu). Sonra her aktif run icin `after=lastSeq` ile
 * baglaniyoruz - ve `after=0` "simdiye kadar olan her sey" oldugu icin
 * SOGUK yeniden baglanma ile sicak reconnect BIREBIR AYNI kod yolu.
 *
 * ## `done` gelince run LISTEDEN DUSMUYOR
 *
 * Bu, canli testte yakalanan gercek bir hatanin duzeltmesi. Ilk hali `done`
 * cercevesinde run'i listeden siliyordu; sonucu:
 *
 *   1. `StreamingMessage` unmount oluyor,
 *   2. kalici `messages` satiri HENUZ yeniden yuklenmemis oluyor,
 *   3. kullanici tamamlanmis yaniti okurken metin EKRANDAN KAYBOLUYOR ve
 *      yerine "hâlâ üretiliyor" yaziyor,
 *   4. birkac yuz ms sonra gecmis gelince metin geri geliyor.
 *
 * Yani gorunur bir yanip sonme ve bir an icin YANLIS bilgi. Simdi run
 * terminal olarak ISARETLENIYOR ama listede kaliyor; `ConversationView`
 * kalici mesaji aldiktan sonra `release()` ile birakiyor. Devir teslim
 * boylece kayipsiz.
 */

interface StreamContextValue {
  /** Bu konusmada su an CALISAN run (terminal olanlar sayilmiyor). */
  runFor(conversationId: string): RunInfo | undefined
  /** Bir asistan mesajina bagli run - terminal olsa bile. */
  runForMessage(messageId: string): RunInfo | undefined
  /** Yeni bir run'i takibe alir. */
  attach(run: RunInfo): void
  /** Terminal bir run'i birakir (kalici mesaj artik ekranda). */
  release(runId: string): void
  /** Iptal ister; okuyucu terminal cerceveyi beklemeye devam ediyor. */
  cancel(runId: string): Promise<void>
  /** Takip edilen tum run'lar (terminal olanlar dahil). */
  runs: RunInfo[]
  /** Acilistaki resume tamamlandi mi. */
  restored: boolean
}

/**
 * Terminal run durumlari.
 *
 * Export edilmis: `Sidebar` da buna ihtiyac duyuyor. Listeyi iki yerde
 * tekrarlamak, birinin gunun birinde yeni bir durumu (ornegin `timeout`)
 * kacirmasi demek olurdu.
 */
export const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'done',
  'failed',
  'cancelled',
  'interrupted',
])

const StreamContext = createContext<StreamContextValue | null>(null)

export function StreamManager({ children }: { children: ReactNode }) {
  const { authenticated, reauthCount } = useSession()
  const [runs, setRuns] = useState<RunInfo[]>([])
  const [restored, setRestored] = useState(false)
  const handles = useRef(new Map<string, StreamHandle>())
  // Liderlik kilitleri: run basina en fazla bir sekme akisi aciyor.
  const leases = useRef(new Map<string, Leadership>())
  // Bu sekme hangi run'lari devralmaya CALISTI - liderlik kaybedilse bile
  // ikinci kez denememek icin.
  const claimed = useRef(new Set<string>())

  const finish = useCallback((runId: string, status: string) => {
    // Okuyucu bitti: soketi birak ama run'i LISTEDE TUT (bkz. docstring).
    handles.current.delete(runId)
    setRuns((current) =>
      current.map((run) =>
        run.id === runId
          ? {
              ...run,
              status: (TERMINAL_STATUSES.has(status) ? status : 'done') as RunInfo['status'],
            }
          : run,
      ),
    )
  }, [])

  const release = useCallback((runId: string) => {
    handles.current.get(runId)?.abort()
    handles.current.delete(runId)
    // Kilidi birak: baska bir sekme ayni run'a lider olabilir.
    leases.current.get(runId)?.release()
    leases.current.delete(runId)
    claimed.current.delete(runId)
    setRuns((current) => current.filter((run) => run.id !== runId))
    // Buffer da birakiliyor: kalici mesaj artik ekranda, ayni metni bellekte
    // ikinci kez tutmak bosa.
    store.drop(runId)
  }, [])

  const attach = useCallback(
    (run: RunInfo) => {
      // Ayni run'i IKINCI kez devralmiyoruz.
      if (claimed.current.has(run.id)) return
      claimed.current.add(run.id)

      setRuns((current) =>
        current.some((item) => item.id === run.id) ? current : [...current, run],
      )
      if (run.queue_position && run.queue_position > 1) {
        store.setQueuePosition(run.id, run.queue_position)
      }

      // Liderlik: TEK sekme akisi aciyor. Tarayici origin basina ~6 baglantiya
      // izin veriyor ve dort sekme kendi akisini acarsa kalan iki baglantiyla
      // her sey yarisiyor - uygulama "yavas" degil TAKILMIS gorunuyor.
      void requestLeadership(run.id).then((lease) => {
        // Devralma sirasinda birakildiysa (kullanici hemen gezindi ve mesaj
        // kalici hale geldi) kilidi hemen geri ver.
        if (!claimed.current.has(run.id)) {
          lease.release()
          return
        }
        leases.current.set(run.id, lease)

        if (!lease.isLeader) {
          // TAKIPCI: hicbir baglanti acmiyor. Delta'lar `BroadcastChannel`
          // uzerinden geliyor (asagidaki dinleyici).
          return
        }

        handles.current.set(
          run.id,
          streamRun(run.id, {
            // LIDER yayin yapiyor: takipci sekmeler ayni metni hicbir
            // baglanti acmadan goruyor.
            onDelta: (text, seq) => publish({ type: 'run_delta', runId: run.id, text, seq }),
            onQueue: (position) => publish({ type: 'run_queue', runId: run.id, position }),
            onDone: (status) => {
              publish({ type: 'run_status', runId: run.id, status })
              finish(run.id, status)
              // SESLI MESAJIN yaniti otomatik okunuyor. Yalnizca LIDER
              // sekmede (burasi zaten lider yolu): dort sekme ayni yaniti
              // ust uste okumasin. Yalnizca basariyla biten yanit - yarim
              // kalmis bir yaniti okumak yaniltirdi.
              if (status === 'done' && consumeVoiceReply()) void speakReply(run)
            },
            // Sunucu kod calistirmayi ISTEMCIYE devretti: kod tarayicida
            // Pyodide worker'inda kosuyor ve sunucu bu sonucu BEKLIYOR
            // (GPU worker thread'i blokta). Bu yuzden yanit vermek hizli
            // olmak zorunda - gecikirse sunucu araçsiz devam ediyor.
            //
            // YALNIZCA lider sekmede: takipciler `onToolRequest` almiyor,
            // aksi halde dort sekme ayni kodu calistirip ayni sonucu
            // gonderirdi (ilki kazanir, ucu bosa 13.5 MB indirir).
            onToolRequest: (request) => void handleToolRequest(run.id, request),
            // Yeniden baglanma tukendi: run'i terminal saymiyoruz (sunucuda
            // devam ediyor olabilir) ama okuyucu yok. Hata mesaji store'da.
            onGiveUp: () => handles.current.delete(run.id),
          }),
        )
      })
    },
    [finish],
  )

  // Acilista, her yeni giriste ve YENIDEN DOGRULAMADAN SONRA yarim kalan
  // isleri devral.
  //
  // `reauthCount` sart: oturum bitip modal acildiginda okuyucu birakiliyor
  // ama `authenticated` hic `false` olmuyor (modal mevcut gorunumun uzerine
  // aciliyor). O yuzden baska bir tetikleyici yok ve bu olmadan kullanici
  // yeniden dogruladiktan sonra yaniti asla gormezdi - sayfayi elle
  // yenilemesi gerekirdi.
  useEffect(() => {
    if (!authenticated) {
      setRestored(false)
      return
    }
    let cancelled = false
    activeRuns()
      .then(({ items }) => {
        if (cancelled) return
        for (const run of items) attach(run)
      })
      .catch(() => {
        // Basarisiz olsa bile uygulama calismali: kullanici yeni mesaj
        // gonderebiliyor, yalnizca yarim kalan is otomatik devralinmiyor.
      })
      .finally(() => {
        if (!cancelled) setRestored(true)
      })
    return () => {
      cancelled = true
    }
  }, [authenticated, reauthCount, attach])

  // Diger sekmelerden gelen mesajlar.
  //
  // TAKIPCI sekme (lider olmayan) metni buradan aliyor ve hicbir baglanti
  // acmiyor. Lider sekme kendi yayinini da GORMUYOR - `BroadcastChannel`
  // gonderen sekmeye teslim etmiyor, yani metin iki kez eklenmiyor.
  useEffect(() => {
    return listen((message) => {
      switch (message.type) {
        case 'run_delta':
          // Yalnizca TAKIPCI uyguluyor: bu sekme lidersey metni zaten
          // okuyucudan aldi.
          if (!handles.current.has(message.runId)) {
            store.appendText(message.runId, message.text, message.seq)
          }
          break
        case 'run_queue':
          store.setQueuePosition(message.runId, message.position)
          break
        case 'run_status':
          if (!handles.current.has(message.runId)) finish(message.runId, message.status)
          break
        case 'logged_out':
          // Baska sekmede cikis yapildi: okuyuculari birak. Oturum durumunu
          // `SessionProvider` kendi gorunurluk tazelemesiyle ogreniyor.
          for (const handle of handles.current.values()) handle.abort()
          handles.current.clear()
          break
        default:
          break
      }
    })
  }, [finish])

  // Sayfa kapanirken soketleri birak. Uretim SUNUCUDA devam ediyor - bu
  // yalnizca temizlik, iptal DEGIL.
  useEffect(() => {
    const map = handles.current
    const locks = leases.current
    return () => {
      for (const handle of map.values()) handle.abort()
      map.clear()
      // Kilitleri de birak: aksi halde bu sekme kapandiktan sonra baska bir
      // sekme lider olamaz. (Tarayici kilidi sayfa kapanisinda zaten
      // biraksa da, SPA icinde unmount olan bir agacta bunu beklemiyoruz.)
      for (const lease of locks.values()) lease.release()
      locks.clear()
    }
  }, [])

  const cancel = useCallback(async (runId: string) => {
    // Okuyucuyu KAPATMIYORUZ: sunucu `cancelled` durumunu ve terminal
    // `done` cercevesini gonderecek. Simdi kapatmak, kullaniciya iptalin
    // gerceklestigini gostermeden birakmak olurdu.
    await cancelRun(runId).catch(() => undefined)
  }, [])

  const value = useMemo<StreamContextValue>(
    () => ({
      runs,
      restored,
      attach,
      release,
      cancel,
      runFor: (conversationId) =>
        runs.find(
          (run) => run.conversation_id === conversationId && !TERMINAL_STATUSES.has(run.status),
        ),
      runForMessage: (messageId) => runs.find((run) => run.assistant_message_id === messageId),
    }),
    [runs, restored, attach, release, cancel],
  )

  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>
}

export function useStreams(): StreamContextValue {
  const value = useContext(StreamContext)
  if (!value) throw new Error('useStreams, StreamManager icinde cagrilmali')
  return value
}

/**
 * Tamamlanan yaniti sesli okur.
 *
 * Metin akistan DEGIL sunucudan aliniyor: akis metni dusunme blogunu ve
 * arac karti isaretlerini de tasiyor; kaydedilmis `content` ise kullanicinin
 * gordugu temiz yanit. Hata sessiz: otomatik okuma bir kolaylik, basarisiz
 * olursa kullanici mesajdaki okuma dugmesini kullanabilir.
 */
async function speakReply(run: RunInfo): Promise<void> {
  try {
    const list = await getMessages(run.conversation_id)
    const message = list.items.find((item) => item.id === run.assistant_message_id)
    const text = message?.content?.trim()
    if (text) await speakViaServer(text)
  } catch {
    // Kolaylik ozelligi: sessizce vazgec.
  }
}
